/**
 * Integrytas Group - Base App (MOBILE)
 * Page: CMM.LOCKS — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/CmmLocksPage.tsx. A DISTINCT
 * presentation (KPI summary + dense card list + facet chips + bottom-sheet detail)
 * over the SAME data/logic layer only:
 *   - reads via apiDb (cmm_lease live leases + a cmm_managed_entity roll-up),
 *   - force-release routes through the CMM module's OWN sanctioned path — the
 *     cmm_release RPC — exactly like the desktop page. That is a concurrency-control
 *     operation via the module's function, not a business-data table write, so it
 *     does not (and must not) go through a raw apiDb insert/update/delete.
 *
 * Read-mostly by design: the leases list is the "locks". Editing which entities are
 * lease-managed (+ TTL) is a low-frequency admin task and stays on the desktop —
 * mirroring how MobileOrgLookupPage leaves translation editing to the desktop. The
 * managed-entity count is surfaced here as context only. It never re-renders the
 * desktop grid; §16.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Lock, Unlock, X } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useFormatDateTime } from '../../lib/format_date';
import {
  MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow,
  MobileDetailRow, MobileFilterChips, MobileStatusPill,
  type MobilePillTone,
} from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

interface MobileLeaseRow {
  cmm_lease_id: number;
  // entity_name is the managed table name and record_id is the natural id of the
  // locked row — both natural identifiers, never surrogate keys. holder_user_id is a
  // surrogate id and is deliberately NOT surfaced; holder_name is shown instead.
  entity_name: string;
  record_id: string;
  holder_name: string | null;
  acquired_at: string;
  expires_at: string;
}

// A lease within this window of its expiry is flagged "expiring soon".
const EXPIRING_SOON_MS = 60_000;

type EntityFacet = string; // 'all' or a distinct entity_name

const PAGE_CODE = 'CMM.LOCKS';

export function MobileCmmLocksPage({ session }: MobilePageComponentProps) {
  // §6/§17: releasing another user's lock / restoring or resolving shared
  // state is page-scoped, so the affordance follows csm_role_page_access.
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const [leases, setLeases] = useState<MobileLeaseRow[]>([]);
  const [managedCount, setManagedCount] = useState(0);
  const [filter, setFilter] = useState('');
  const [entityFacet, setEntityFacet] = useState<EntityFacet>('all');
  const [selected, setSelected] = useState<MobileLeaseRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const [leaseRes, managedRes] = await Promise.all([
        apiDb
          .from('cmm_lease')
          .select('cmm_lease_id, entity_name, record_id, holder_user_id, holder_name, acquired_at, expires_at')
          .order('expires_at', { ascending: false })
          .limit(100),
        apiDb.from('cmm_managed_entity').select('cmm_managed_entity_id').eq('is_active', 'Y'),
      ]);
      if (leaseRes.error) throw leaseRes.error;
      if (managedRes.error) throw managedRes.error;

      // Live leases only (mirrors the desktop's expiry filter).
      const now = Date.now();
      const rows = ((leaseRes.data ?? []) as MobileLeaseRow[]).filter(
        (l) => new Date(l.expires_at).getTime() > now,
      );
      setLeases(rows);
      setManagedCount((managedRes.data ?? []).length);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Load on mount. No _tl display resolution here, so no locale dependency.
  useEffect(() => { void reload(); }, []);

  const entityFacets = useMemo<{ key: EntityFacet; label: string }[]>(() => {
    const distinct = [...new Set(leases.map((l) => l.entity_name).filter(Boolean))].sort();
    return [
      { key: 'all', label: t('mobile.cmm.all', 'All') },
      ...distinct.map((e) => ({ key: e, label: e })),
    ];
  }, [leases]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return leases.filter((l) => {
      if (entityFacet !== 'all' && l.entity_name !== entityFacet) return false;
      if (!q) return true;
      return (
        l.entity_name.toLowerCase().includes(q) ||
        l.record_id.toLowerCase().includes(q) ||
        (l.holder_name ?? '').toLowerCase().includes(q)
      );
    });
  }, [leases, filter, entityFacet]);

  const isExpiringSoon = (l: MobileLeaseRow) =>
    new Date(l.expires_at).getTime() - Date.now() <= EXPIRING_SOON_MS;

  const statusPill = (l: MobileLeaseRow): { tone: MobilePillTone; label: string } =>
    isExpiringSoon(l)
      ? { tone: 'wait', label: t('mobile.cmm.expiring', 'Expiring') }
      : { tone: 'info', label: t('mobile.cmm.held', 'Held') };

  const holderText = (l: MobileLeaseRow) => l.holder_name ?? t('common.unknown', 'Unknown');
  const recordText = (l: MobileLeaseRow) => `${l.entity_name} #${l.record_id}`;

  const forceRelease = async (l: MobileLeaseRow) => {
    const ok = window.confirm(
      t('mobile.cmm.confirm_release', 'Force-release the lease on {{record}} held by {{holder}}?', {
        record: recordText(l),
        holder: holderText(l),
      }),
    );
    if (!ok) return;
    setActionBusy(true);
    setError(null);
    try {
      // The CMM module's sanctioned release path (mirrors the desktop page).
      const { error: e } = await apiDb.rpc('cmm_release', { p_entity: l.entity_name, p_record: l.record_id });
      if (e) throw e;
      setSelected(null);
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.cmm.title', 'Concurrency Locks')}
        description={t('mobile.cmm.description', 'Live record leases held across the platform, with their holders.')}
      />

      <MobileStatStrip
        stats={[
          { value: leases.length.toLocaleString(), label: t('mobile.cmm.kpi_active', 'Active') },
          { value: managedCount.toLocaleString(), label: t('mobile.cmm.kpi_managed', 'Managed') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.cmm.search', 'Search leases')}
          aria-label={t('mobile.cmm.search', 'Search leases')}
        />
      </div>

      <MobileFilterChips
        chips={entityFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => entityFacet === v}
        onSelect={(v) => setEntityFacet(v)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.cmm.none', 'No active leases')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((l) => {
            const pill = statusPill(l);
            return (
              <MobileCardRow
                key={l.cmm_lease_id}
                lead={<Lock />}
                leadTone={isExpiringSoon(l) ? 'amber' : 'slate'}
                title={recordText(l)}
                subtitle={`${holderText(l)} · ${formatDateTime(l.acquired_at)}`}
                trailing={<MobileStatusPill tone={pill.tone}>{pill.label}</MobileStatusPill>}
                chevron
                onClick={() => setSelected(l)}
              />
            );
          })}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.cmm.detail', 'Lease detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Lock size={20} /></span>
              <span>
                <div className="m-nm">{recordText(selected)}</div>
                <div className="m-em">{holderText(selected)}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow label={t('mobile.cmm.entity', 'Entity')} value={selected.entity_name} />
              <MobileDetailRow label={t('mobile.cmm.record', 'Record')} value={selected.record_id} />
              <MobileDetailRow label={t('mobile.cmm.holder', 'Held by')} value={holderText(selected)} />
              <MobileDetailRow
                label={t('mobile.cmm.status', 'Status')}
                value={<MobileStatusPill tone={statusPill(selected).tone}>{statusPill(selected).label}</MobileStatusPill>}
              />
              <MobileDetailRow label={t('mobile.cmm.since', 'Since')} value={formatDateTime(selected.acquired_at)} />
              <MobileDetailRow label={t('mobile.cmm.expires', 'Expires')} value={formatDateTime(selected.expires_at)} />
            </MobileContentCard>
            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className="m-btn m-btn-danger"
                disabled={actionBusy}
                onClick={() => void forceRelease(selected)}
              >
                <Unlock size={16} />
                {t('mobile.cmm.force_release', 'Force release')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
